import {
  DEFAULT_LIMIT,
  fetchAllCursorPages,
  ifMatchHeaders,
  schedulingDelete,
  schedulingGet,
  schedulingPost,
  schedulingPut,
  versionBody,
  idempotencyHeaders,
} from "./client";
import { axios } from "../../../lib/axios";
import { schedulingDataChanged } from "../../../lib/schedulingCache";
import { appointmentEncounterPayload } from "./appointmentEncounterPayload";

function withIfMatch(version, extra = {}) {
  return {
    headers: {
      ...ifMatchHeaders(version),
      ...(extra.headers || {}),
    },
  };
}

export async function confirmAppointment(body, idempotencyKey) {
  if (body.bookingContext) {
    const source = await axios.get(`encounter/${encodeURIComponent(body.bookingContext.sourceEncounterId)}`);
    if (source?.data?.orgId !== body.organizationRef || source?.data?.subject?.reference !== `Patient/${body.patientRefs?.[0]}`) {
      throw new Error("The source encounter does not match this appointment.");
    }
  }
  const created = await schedulingPost("appointment", body, {
    headers: idempotencyHeaders(idempotencyKey || `appointment:${body.holdId}`),
  });
  if (body.bookingContext) {
    try {
      await linkScheduledAppointment({ organizationRef: body.organizationRef, sourceEncounterId: body.bookingContext.sourceEncounterId, appointmentId: created.appointmentId });
    } catch (error) {
      const pending = new Error("Appointment booked. Could not link it to the source encounter. Retry to finish linking.");
      pending.bookedAppointment = created;
      throw pending;
    }
  }
  return created;
}

export function linkScheduledAppointment({ organizationRef, sourceEncounterId, appointmentId }) {
  return axios.put(`encounter/${encodeURIComponent(sourceEncounterId)}`, {
    scheduledAppointmentIds: [appointmentId],
    serviceProvider: { reference: `Organization/${organizationRef}`, type: "Organization" },
  });
}

export function updateAppointmentDetails({ organizationRef, appointmentId, bookingContext, version }) {
  return schedulingPut("appointment/details", { organizationRef, appointmentId, bookingContext }, withIfMatch(version));
}

export async function launchAppointmentEncounter({ appointment, organizationRef, practitionerRef }) {
  if (appointment.encounterId) {
    const existing = await axios.get(`encounter/${encodeURIComponent(appointment.encounterId)}`);
    if (existing?.data?.id !== appointment.encounterId || existing.data.orgId !== organizationRef || existing.data.appointmentId !== appointment.appointmentId) throw new Error("The linked encounter does not match this appointment.");
    return existing.data;
  }
  const source = await axios.get(`encounter/${encodeURIComponent(appointment.bookingContext.sourceEncounterId)}`);
  const response = await axios.post("encounter", appointmentEncounterPayload({ appointment, source: source.data, organizationRef, practitionerRef }));
  schedulingDataChanged();
  const encounter = response?.data;
  if (!encounter?.id || encounter.appointmentId !== appointment.appointmentId || encounter.sourceEncounterId !== appointment.bookingContext.sourceEncounterId) {
    throw new Error("The appointment encounter could not be confirmed. Refresh and try again.");
  }
  return encounter;
}

export function listAppointments({
  organizationRef,
  scope,
  excludedOrganizationRefs,
  appointmentId,
  scheduleId,
  patientRef,
  practitionerRef,
  serviceCategoryId,
  status,
  startsAt,
  endsAt,
  cursor,
  limit = DEFAULT_LIMIT,
  signal,
  consistency,
}) {
  return schedulingGet("appointment", {
    organizationRef,
    scope,
    excludedOrganizationRefs,
    appointmentId,
    scheduleId,
    patientRef,
    practitionerRef,
    serviceCategoryId,
    status,
    startsAt,
    endsAt,
    cursor,
    limit,
  }, { signal, consistency });
}

export async function listAllAppointments(params) {
  return fetchAllCursorPages({
    signal: params.signal,
    itemsKey: "appointments",
    fetchPage: (cursor) => listAppointments({ ...params, cursor }),
  });
}

export function getAppointment({ organizationRef, appointmentId, consistency, signal }) {
  return schedulingGet("appointment", { organizationRef, appointmentId }, ...(consistency || signal ? [{ consistency, signal }] : []));
}

export function approveAppointment({ organizationRef, appointmentId, version }) {
  return schedulingPost(
    "appointment/approve",
    { organizationRef, appointmentId },
    withIfMatch(version)
  );
}

export function rejectAppointment({ organizationRef, appointmentId, version }) {
  return schedulingPost(
    "appointment/reject",
    { organizationRef, appointmentId },
    withIfMatch(version)
  );
}

export function markNoShowAppointment({ organizationRef, appointmentId, version }) {
  return schedulingPost(
    "appointment/noShow",
    { organizationRef, appointmentId },
    withIfMatch(version)
  );
}

export function transferAppointment({ organizationRef, appointmentId, scheduleIds, version }) {
  return schedulingPut(
    "appointment/practitioners",
    { organizationRef, appointmentId, scheduleIds },
    withIfMatch(version)
  );
}

export function rescheduleAppointment({ organizationRef, appointmentId, startsAt, endsAt, scheduleIds, version, blockOriginal }) {
  return schedulingPut(
    "appointment",
    { organizationRef, appointmentId, startsAt, endsAt, ...(scheduleIds ? { scheduleIds } : {}), ...(blockOriginal ? { blockOriginal: true } : {}) },
    withIfMatch(version)
  );
}

export function cancelAppointment({ organizationRef, appointmentId, version, blockOriginal }) {
  return schedulingDelete(
    "appointment",
    {
      organizationRef,
      appointmentId,
      ...versionBody(version),
      ...(blockOriginal ? { blockOriginal: true } : {}),
    },
    withIfMatch(version)
  );
}
